import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { logout } from "../slices/authSlice";
import {
  useGetNotificationsQuery,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
  useDeleteNotificationMutation,
  useDeleteAllNotificationsMutation
} from "../slices/notificationApiSlice";
import { toggleTheme } from "../slices/themeSlice";
import { setLanguage } from "../slices/languageSlice";
import { FaBell, FaShoppingCart, FaMoon, FaSun } from "react-icons/fa";
import { useConfirm } from "./ui/ConfirmProvider";


const NotificationDropdown = ({ userInfo }) => {
  const confirm = useConfirm();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isTh = i18n.language?.startsWith("th");

  const { data: notifications, refetch } = useGetNotificationsQuery(undefined, { skip: !userInfo });
  const [markAsRead] = useMarkAsReadMutation();
  const [markAllAsRead] = useMarkAllAsReadMutation();
  const [deleteNotification] = useDeleteNotificationMutation();
  const [deleteAllNotifications] = useDeleteAllNotificationsMutation();

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);

    // 🆕 Listen for real-time notifications from socket → refetch
    const onSocketNotif = () => refetch();
    window.addEventListener("workflow_notification", onSocketNotif);

    return () => {
      document.removeEventListener("mousedown", handler);
      window.removeEventListener("workflow_notification", onSocketNotif);
    };
  }, [refetch]);

  const notifsList = notifications?.data || notifications || [];
  const unreadCount = notifsList.filter(n => !n.isRead).length;

  const handleNotificationClick = async (notif) => {
    if (!notif.isRead) {
      await markAsRead({ id: notif._id || notif.id, scope: notif.scope || 'personal' });
      refetch();
    }
    setIsOpen(false);

    if (notif.type === 'stock_request' || notif.type === 'issue') {
      navigate('/componentuserissuelist');
    } else if (notif.type === 'payment_success') {
      navigate('/admin/invoices');
    } else if (notif.type === 'PURCHASE_RQ_SUBMITTED') {
      // หัวหน้าจัดซื้อ — มีใบขอซื้อรอรวม
      navigate('/purchasing/orders?tab=inbox');
    } else if (notif.type === 'PURCHASE_RQ_CANCELLED') {
      navigate(notif.related_id ? `/tools/bom2buy/${notif.related_id}` : '/tools/bom2buy');
    } else if (notif.type && notif.type.startsWith('PURCHASE_PO_')) {
      navigate(notif.related_id ? `/purchasing/orders/${notif.related_id}` : '/purchasing/orders');
    } else if (notif.type && notif.type.startsWith('TASK_')) {
      // ระบบงาน — related_id = เลขงาน
      navigate(notif.related_id ? `/tasks/${notif.related_id}` : '/tasks');
    } else if (notif.type === 'CHAT_MENTION') {
      // มีคนแท็กในแชทกลุ่ม — related_id = เลขห้อง
      navigate(notif.related_id ? `/chat/${notif.related_id}` : '/chat');
    } else if (notif.type === 'TOOL_LOAN_DUE_ME') {
      // ยืมเครื่องมือครบกำหนดคืน — คนยืมไปหน้าเครื่องมือของฉัน · คนตามของไปหน้ายืมเครื่องมือ
      navigate('/my-tools');
    } else if (notif.type && notif.type.startsWith('KICAD_UPDATE_')) {
      // คำขอแก้ไฟล์ KiCad — ผู้อนุมัติ/คนขอ ไปหน้าคิว
      navigate('/tools/kicad-updates');
    } else if (notif.type === 'TOOL_LOAN_DUE') {
      navigate('/production/tools');
    } else if (notif.ref_type === 'workflow' || (notif.type && notif.type.includes('WORKFLOW'))) {
      // ระบบ Workflow เก่าเลิกใช้แล้ว — แจ้งเตือนเก่าพาไปหน้างาน
      navigate('/tasks');
    }
  };

  if (!userInfo) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setIsOpen((o) => !o)}
        className="relative flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-600 dark:text-slate-300 transition-colors border border-slate-200 dark:border-zinc-700"
      >
        <FaBell />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-zinc-900 rounded-xl shadow-xl border border-slate-200 dark:border-zinc-700 py-2 z-[10000] max-h-[32rem] flex flex-col">
          <div className="flex justify-between items-center px-4 py-2 border-b border-slate-100 dark:border-zinc-800">
            <span className="font-bold text-slate-800 dark:text-white">
              {t("header.notifications")}
            </span>
            <div className="flex gap-2">
              <button
                onClick={async () => { await markAllAsRead(); refetch(); }}
                className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                title={t("header.markAllRead")}
              >
                {t("header.markAllRead")}
              </button>
              <button
                onClick={async () => {
                  if (await confirm(t("header.deleteAllConfirm"), { danger: true })) {
                    await deleteAllNotifications();
                    refetch();
                  }
                }}
                className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 font-medium"
                title={t("header.clearAll")}
              >
                {t("header.clearAll")}
              </button>
            </div>
          </div>

          <div className="overflow-y-auto">
            {notifsList.length === 0 ? (
              <div className="px-4 py-4 text-center text-sm text-slate-500">
                {t("header.noNotifications")}
              </div>
            ) : (
              notifsList.map((n) => (
                <div
                  key={n._id || n.id || Math.random()}
                  className={`relative group px-4 py-3 border-b border-slate-100 dark:border-zinc-800 hover:bg-slate-50 dark:hover:bg-zinc-800 transition-colors ${!n.isRead ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}
                >
                  <div
                    className="cursor-pointer pr-6"
                    onClick={() => handleNotificationClick(n)}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className={`text-sm ${!n.isRead ? 'font-semibold text-slate-800 dark:text-white' : 'text-slate-600 dark:text-slate-300'}`}>
                        {n.title || t("header.newMessage")}
                      </span>
                      {!n.isRead && <span className="w-2 h-2 rounded-full bg-blue-500 mt-1 flex-shrink-0"></span>}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {n.message || n.body}
                    </div>
                  </div>

                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      await deleteNotification({ id: n._id || n.id, scope: n.scope || 'personal' });
                      refetch();
                    }}
                    className="absolute top-3 right-3 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    title={t("header.delete")}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const stockCartState = useSelector((state) => state.stockcart);
  const cartItemsCount = stockCartState?.stockcartItems?.length || 0;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const theme = useSelector((state) => state.theme?.theme || "light");

  const isTh = i18n.language?.startsWith("th");

  // TH / EN (22 ต.ค. 69) — จำต่อเครื่อง · เปลี่ยนทั้ง i18next และ state.language ของหน้าเก่าพร้อมกัน
  const toggleLang = () => {
    dispatch(setLanguage(isTh ? "en" : "th"));
  };

  const logoutHandler = () => {
    dispatch(logout());
    navigate("/login");
  };

  return (
    <header id="app-header" className="bg-white/80 dark:bg-[#1c1c1e]/80 backdrop-blur-2xl shadow-sm border-b border-slate-200 dark:border-[#38383a] sticky top-0 z-[999]">
      <div className="w-full px-4 sm:px-8 py-2 md:py-3 flex items-center justify-between">
        {/* Left: Mobile Logo, Back Button & Breadcrumb */}
        <div className="flex-1 flex justify-start items-center gap-3 min-w-0">
          {userInfo && (
            <button
              onClick={() => window.dispatchEvent(new Event('toggle-mobile-nav'))}
              className="flex lg:hidden items-center justify-center w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-700 dark:text-slate-200 transition-colors border border-slate-200 dark:border-zinc-700 flex-shrink-0"
              title={t("header.menu")}
              aria-label="Open menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          )}
          {location.pathname !== '/' && (
            <button
              onClick={() => navigate(-1)}
              className="hidden lg:flex items-center justify-center w-8 h-8 rounded-full bg-[#f5f5f7] hover:bg-[#e5e5ea] dark:bg-[#2c2c2e] dark:hover:bg-[#3a3a3c] text-[#1d1d1f] dark:text-[#f5f5f7] transition-all duration-300 ease-out transform-gpu hover:scale-105 active:scale-95 shadow-sm border border-slate-200/50 dark:border-[#38383a]"
              title="ย้อนกลับ (Go Back)"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <Link to="/" className="flex lg:hidden items-center whitespace-nowrap">
            <img src="/image/Pawin_Logo_transparent.png" alt="PAWIN Logo" className="h-8 object-contain" />
          </Link>
          <span className="hidden lg:block text-slate-500 font-medium">Dashboard</span>
        </div>

        {/* Right: Actions & User Info */}
        <div className="flex-1 flex justify-end items-center gap-2 sm:gap-4 min-w-0">
          {/* 
            TODO: Dark mode toggle temporarily disabled 
            <button
              onClick={() => dispatch(toggleTheme())}
              className="flex-none flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-600 dark:text-slate-300 font-semibold text-sm transition-colors border border-slate-200 dark:border-zinc-700"
              title="Toggle Dark Mode"
            >
              {theme === "dark" ? <FaSun className="w-4 h-4" /> : <FaMoon className="w-4 h-4" />}
            </button>
          */}

          {userInfo && (
            <button
              onClick={() => window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", ctrlKey: true }))}
              className="hidden lg:flex items-center gap-2 h-8 pl-3 pr-2 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-500 dark:text-slate-300 transition-colors border border-slate-200 dark:border-zinc-700"
              title={t("header.searchPages")}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
              </svg>
              <span className="text-xs font-medium">{t("header.search")}</span>
              <kbd className="text-[10px] font-semibold border border-slate-300 dark:border-zinc-600 rounded px-1">⌘K</kbd>
            </button>
          )}



          {/* เปลี่ยนภาษา — ปุ่มบอกภาษาที่จะเปลี่ยนไป (อยู่ไทย เห็น "EN") */}
          <button
            type="button"
            onClick={toggleLang}
            className="flex h-8 min-w-[2.5rem] items-center justify-center rounded-full border border-slate-200 bg-slate-100 px-2 text-xs font-bold text-slate-700 hover:bg-slate-200 dark:border-zinc-700 dark:bg-zinc-800 dark:text-slate-200"
            title={isTh ? "Switch to English" : "เปลี่ยนเป็นภาษาไทย"}
            aria-label={isTh ? "Switch to English" : "เปลี่ยนเป็นภาษาไทย"}
          >
            {isTh ? "EN" : "ไทย"}
          </button>

          {/* ปุ่มงานผลิต · งาน · แชต (ย้ายจากปุ่มลอยมุมขวาล่าง 20 ต.ค. 69 — ทับปุ่มของฟอร์มบน iPad) · components/ui/HeaderSlot.jsx */}
          <div id="header-dock-slot" className="flex items-center gap-2 empty:hidden" />

          {userInfo && (
            <button
              onClick={() => navigate('/componentcartlist')}
              className="relative flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-slate-600 dark:text-slate-300 transition-colors border border-slate-200 dark:border-zinc-700"
              title={t("header.requestCart")}
            >
              <FaShoppingCart />
              {cartItemsCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center justify-center min-w-[16px] h-[16px]">
                  {cartItemsCount}
                </span>
              )}
            </button>
          )}

          <NotificationDropdown userInfo={userInfo} />

          {userInfo ? (
            <div className="flex items-center gap-3">
              <div className="hidden xl:flex flex-col items-end whitespace-nowrap">
                <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 leading-tight">
                  {userInfo.username || userInfo.name || userInfo.email}
                </span>
                <span className="text-xs text-slate-500">{userInfo.role || 'User'}</span>
              </div>
              {/* จอแคบ (มือถือ) เหลือไอคอน — หัวเว็บมีปุ่มงานผลิต/งาน/แชตเพิ่ม 3 ปุ่ม (20 ต.ค. 69) */}
              <button
                onClick={logoutHandler}
                title={t("nav.logout")}
                aria-label={t("nav.logout")}
                className="text-sm bg-slate-100 hover:bg-red-500 text-slate-600 hover:text-white px-2.5 sm:px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap"
              >
                <svg className="w-4 h-4 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12H3m0 0l4-4m-4 4l4 4m6-9V5a2 2 0 012-2h4a2 2 0 012 2v14a2 2 0 01-2 2h-4a2 2 0 01-2-2v-2" />
                </svg>
                <span className="hidden sm:inline">{t("nav.logout")}</span>
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              className="btn-apple-primary text-sm px-4 sm:px-5 py-2 shadow-sm whitespace-nowrap"
            >
              {t("nav.login")}
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
